import React, { useEffect, useMemo, useState } from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Loader2, X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { AccrualDialogProps } from '../types';
import { usersApi, User } from '../../../api/users';

/** Покупателя ищем по нику, email или id — id приходит из RevenueCat. */
function useUserSearch(query: string) {
  const [results, setResults] = useState<User[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 3) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        // Похожее на UUID ищем по id, остальное — обычным поиском.
        const isId = /^[0-9a-fA-F-]{6,}$/.test(term) && term.includes('-');
        const response = await usersApi.getUsers({
          ...(isId ? { id: term } : { search: term }),
          limit: 8,
          isDeleted: 'all',
        });
        if (!cancelled) setResults(response.users);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  return { results, isSearching };
}

export function AccrualDialog({
  isDark,
  isOpen,
  onOpenChange,
  providers,
  formData,
  onFormDataChange,
  onSubmit,
  isEditing,
  isSubmitting,
  formatMoney,
}: AccrualDialogProps) {
  const [userQuery, setUserQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { results, isSearching } = useUserSearch(userQuery);

  const provider = useMemo(
    () => providers.find((p) => p.id === formData.providerId),
    [providers, formData.providerId],
  );
  const currency = provider?.currency || 'UZS';
  const needsRate = currency !== 'UZS';
  const rate = needsRate ? formData.rate || 0 : 1;

  // Пересчёт в шапке формы: сколько это в сумах и что останется после комиссии.
  const amountUzs = (formData.amount || 0) * rate;
  const net = (formData.amount || 0) * (1 - (provider?.commission_percent || 0) / 100);

  useEffect(() => {
    if (!isOpen) {
      setUserQuery('');
      setSelectedUser(null);
      return;
    }
    // При редактировании в форме лежит только id — подтягиваем ник, иначе в
    // поле покупателя висел бы голый uuid.
    if (!formData.userId || selectedUser?.id === formData.userId) return;
    let cancelled = false;
    usersApi
      .getUsers({ id: formData.userId, limit: 1, isDeleted: 'all' })
      .then((response) => {
        if (!cancelled) setSelectedUser(response.users[0] || null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isOpen, formData.userId]);

  const inputCls = cn(
    isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200',
  );
  const labelCls = cn(isDark ? 'text-slate-300' : 'text-slate-700');
  const hintCls = cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500');

  const pickUser = (user: User) => {
    setSelectedUser(user);
    setUserQuery('');
    onFormDataChange((prev) => ({ ...prev, userId: user.id }));
  };

  const clearUser = () => {
    setSelectedUser(null);
    setUserQuery('');
    onFormDataChange((prev) => ({ ...prev, userId: null }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-w-xl', isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white')}>
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#111111]')}>
            {isEditing ? 'Изменить начисление' : 'Добавить начисление'}
          </DialogTitle>
          <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
            Одна продажа у провайдера, чьих платежей нет в базе. Удержанный налог или
            возврат покупателю заводится отрицательной суммой. Премиум пользователю эта
            запись не выдаёт.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={labelCls}>Дата продажи *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => onFormDataChange((prev) => ({ ...prev, date: e.target.value }))}
                className={inputCls}
              />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Провайдер *</Label>
              <Select
                value={formData.providerId}
                onValueChange={(value) =>
                  onFormDataChange((prev) => ({ ...prev, providerId: value }))
                }
              >
                <SelectTrigger className={inputCls}>
                  <SelectValue placeholder="Выберите провайдера" />
                </SelectTrigger>
                <SelectContent className={cn(isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white')}>
                  {providers.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className={cn('grid gap-4', needsRate ? 'grid-cols-2' : 'grid-cols-1')}>
            <div className="space-y-2">
              <Label className={labelCls}>Сумма, {currency} *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.amount || ''}
                onChange={(e) =>
                  onFormDataChange((prev) => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))
                }
                className={inputCls}
              />
            </div>
            {needsRate && (
              <div className="space-y-2">
                <Label className={labelCls}>Курс {currency} → сум *</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={formData.rate || ''}
                  onChange={(e) =>
                    onFormDataChange((prev) => ({ ...prev, rate: parseFloat(e.target.value) || 0 }))
                  }
                  className={inputCls}
                />
              </div>
            )}
          </div>

          {!!formData.amount && (
            <div
              className={cn(
                'rounded-xl px-4 py-3 space-y-1',
                isDark ? 'bg-slate-800/60' : 'bg-slate-50',
              )}
            >
              <div className="flex items-center justify-between">
                <span className={hintCls}>Чистое (−{provider?.commission_percent || 0}%)</span>
                <span className="text-sm font-semibold text-emerald-500">
                  {formatMoney(net, currency)}
                </span>
              </div>
              {needsRate && (
                <div className="flex items-center justify-between">
                  <span className={hintCls}>В сумах по курсу</span>
                  <span className={cn('text-sm font-semibold', isDark ? 'text-white' : 'text-[#111111]')}>
                    {formatMoney(amountUzs, 'UZS')}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label className={labelCls}>Покупатель</Label>
            {formData.userId ? (
              <div
                className={cn(
                  'flex items-center justify-between rounded-xl px-3 py-2',
                  isDark ? 'bg-slate-800' : 'bg-slate-100',
                )}
              >
                <span className={cn('text-sm', isDark ? 'text-slate-200' : 'text-slate-700')}>
                  {selectedUser
                    ? `${selectedUser.username}${selectedUser.email ? ` · ${selectedUser.email}` : ''}`
                    : formData.userId}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={clearUser}
                  className="h-7 w-7"
                >
                  <X className="w-4 h-4 text-slate-400" />
                </Button>
              </div>
            ) : (
              <>
                <Input
                  type="text"
                  placeholder="Ник, email или id пользователя"
                  value={userQuery}
                  onChange={(e) => setUserQuery(e.target.value)}
                  className={inputCls}
                />
                {isSearching && <p className={hintCls}>Ищем…</p>}
                {results.length > 0 && (
                  <div
                    className={cn(
                      'rounded-xl border overflow-hidden',
                      isDark ? 'border-slate-700' : 'border-slate-200',
                    )}
                  >
                    {results.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => pickUser(user)}
                        className={cn(
                          'w-full text-left px-3 py-2 text-sm transition-colors',
                          isDark
                            ? 'text-slate-200 hover:bg-slate-800'
                            : 'text-slate-700 hover:bg-slate-50',
                        )}
                      >
                        {user.username}
                        {user.email ? ` · ${user.email}` : ''}
                        {user.isSubscribed ? ' · премиум' : ''}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="space-y-2">
            <Label className={labelCls}>Комментарий</Label>
            <Input
              type="text"
              placeholder="Например: iap_halfyearly_6, отчёт RevenueCat"
              value={formData.comment || ''}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, comment: e.target.value }))}
              className={inputCls}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className={cn(isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200')}
          >
            Отмена
          </Button>
          <Button
            onClick={onSubmit}
            disabled={isSubmitting}
            className="bg-[#C9A227] hover:bg-[#C9A227]/90"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEditing ? 'Сохранить' : 'Добавить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
